import { Button } from "@dash/form";
import { useTheme } from "@emotion/react";
import { useIntersection } from "@mantine/hooks";
import React, { useState } from "react";
import { MdAdd, MdArrowDownward, MdArrowUpward, MdClose, MdDeleteOutline, MdPlayArrow } from "react-icons/md";

import { ReportCell, ReportCellResult } from "@megaron/crm-contracts";
import { newUuid } from "@megaron/uuid";

import { IconButton, RemoveCellDialog, VisibleToggle, WidthPicker } from "../CellEditor";
import { CellResult, hasOutput } from "./CellResult";

const monoFont = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Props = {
  cells: ReportCell[];
  savedCells: ReportCell[];
  results: Map<string, ReportCellResult>;
  isRunning: boolean;
  isSaving: boolean;
  onChange: (cells: ReportCell[]) => void;
  onRun: () => void;
  onClose: () => void;
};

export const NotebookView: React.FC<Props> = ({
  cells,
  savedCells,
  results,
  isRunning,
  isSaving,
  onChange,
  onRun,
  onClose,
}) => {
  const theme = useTheme();
  const [removing, setRemoving] = useState<ReportCell | null>(null);
  const isDirty = JSON.stringify(cells) !== JSON.stringify(savedCells);
  const saved = new Map(savedCells.map((cell) => [cell.uuid, cell]));

  const update = (index: number, cell: ReportCell) => onChange(cells.map((c, i) => (i === index ? cell : c)));
  const move = (index: number, offset: -1 | 1) => {
    const next = [...cells];
    next.splice(index + offset, 0, ...next.splice(index, 1));
    onChange(next);
  };
  const insert = (index: number) =>
    onChange([
      ...cells.slice(0, index),
      { uuid: newUuid(), title: "", width: "full", visible: true, code: "" },
      ...cells.slice(index),
    ]);

  return (
    <div
      css={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.75rem", paddingBottom: "3rem" }}
    >
      <NotebookToolbar
        status={isRunning ? "running" : isDirty ? "dirty" : "saved"}
        isSaving={isSaving}
        isRunning={isRunning}
        onClose={onClose}
        onRun={onRun}
      />
      {cells.map((cell, index) => (
        <React.Fragment key={cell.uuid}>
          <InsertLine onInsert={() => insert(index)} />
          <CodeCell
            cell={cell}
            result={results.get(cell.uuid)}
            isStale={saved.get(cell.uuid)?.code !== cell.code}
            isRunning={isRunning}
            isFirst={index === 0}
            isLast={index === cells.length - 1}
            onChange={(next) => update(index, next)}
            onMove={(offset) => move(index, offset)}
            onRemove={() => setRemoving(cell)}
            onRun={onRun}
          />
        </React.Fragment>
      ))}
      <div css={{ display: "flex", gap: "0.5rem" }}>
        <Gutter />
        <button
          type="button"
          onClick={() => insert(cells.length)}
          css={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.375rem",
            padding: "0.875rem",
            border: `1px dashed ${theme.colors.border}`,
            borderRadius: theme.smallBorderRadius,
            background: "transparent",
            color: theme.colors.secondaryText,
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:hover": {
              color: theme.colors.primary,
              borderColor: theme.colors.primary,
              background: theme.colors.primaryLight,
            },
          }}
        >
          <MdAdd /> Dodaj blok
        </button>
      </div>

      {removing && (
        <RemoveCellDialog
          title={removing.title}
          onClose={() => setRemoving(null)}
          onRemove={() => {
            onChange(cells.filter((cell) => cell.uuid !== removing.uuid));
            setRemoving(null);
          }}
        />
      )}
    </div>
  );
};

const statusLabels = { running: "Uruchamianie…", dirty: "Niezapisane zmiany", saved: "Zapisano" } as const;

type ToolbarProps = {
  status: keyof typeof statusLabels;
  isSaving: boolean;
  isRunning: boolean;
  onClose: () => void;
  onRun: () => void;
};

const NotebookToolbar: React.FC<ToolbarProps> = (props) => {
  const { ref, entry } = useIntersection({ threshold: 1, rootMargin: "-12px 0px 0px 0px" });
  const stickyRect = entry?.isIntersecting === false ? entry.boundingClientRect : undefined;

  return (
    <div css={{ marginLeft: "40px" }}>
      <div ref={ref} css={{ visibility: stickyRect ? "hidden" : "visible" }}>
        <ToolbarBar {...props} />
      </div>
      {stickyRect && (
        <div css={{ position: "fixed", top: "0.75rem", left: stickyRect.left, width: stickyRect.width, zIndex: 3 }}>
          <ToolbarBar {...props} />
        </div>
      )}
    </div>
  );
};

const ToolbarBar: React.FC<ToolbarProps> = ({ status, isSaving, isRunning, onClose, onRun }) => {
  const theme = useTheme();
  const statusColor = { running: theme.colors.primary, dirty: theme.colors.warning, saved: theme.colors.success }[
    status
  ];

  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.5rem 0.5rem 0.5rem 1.125rem",
        background: "white",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.largeBorderRadius,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06)",
      }}
    >
      <span
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flex: 1,
          fontSize: "0.8125rem",
          color: theme.colors.secondaryText,
          whiteSpace: "nowrap",
        }}
      >
        <span css={{ width: "8px", height: "8px", borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
        {statusLabels[status]}
      </span>
      <span css={{ width: "1px", alignSelf: "stretch", background: theme.colors.border }} />
      <Button variant="text" icon={<MdClose />} onClick={onClose} isDisabled={isSaving}>
        Zamknij
      </Button>
      <Button icon={<MdPlayArrow />} onClick={onRun} isLoading={isSaving || isRunning}>
        Zapisz i uruchom
      </Button>
    </div>
  );
};

const CellBox: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();

  return (
    <div
      css={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.smallBorderRadius,
        background: "white",
        ":focus-within": { borderColor: theme.colors.primary },
      }}
    >
      {children}
    </div>
  );
};

const CodeEditor: React.FC<{ code: string; onChange: (code: string) => void; onRun: () => void }> = ({
  code,
  onChange,
  onRun,
}) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      e.preventDefault();
      onRun();
      return;
    }
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      const target = e.currentTarget;
      const { selectionStart, selectionEnd } = target;
      onChange(`${code.slice(0, selectionStart)}    ${code.slice(selectionEnd)}`);
      requestAnimationFrame(() => target.setSelectionRange(selectionStart + 4, selectionStart + 4));
    }
  };

  return (
    <textarea
      value={code}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      spellCheck={false}
      placeholder="# kod Python"
      rows={Math.max(code.split("\n").length, 2)}
      css={{
        margin: 0,
        padding: "0.625rem 1rem",
        border: "none",
        background: "transparent",
        color: "#1d1c1a",
        fontFamily: monoFont,
        fontSize: "0.8125rem",
        lineHeight: 1.6,
        resize: "none",
        tabSize: 4,
        whiteSpace: "pre",
        overflowX: "auto",
        ":focus": { outline: "none" },
      }}
    />
  );
};

const CodeCell: React.FC<{
  cell: ReportCell;
  result: ReportCellResult | undefined;
  isStale: boolean;
  isRunning: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (cell: ReportCell) => void;
  onMove: (offset: -1 | 1) => void;
  onRemove: () => void;
  onRun: () => void;
}> = ({ cell, result, isStale, isRunning, isFirst, isLast, onChange, onMove, onRemove, onRun }) => {
  const theme = useTheme();

  return (
    <div css={{ display: "flex", gap: "0.5rem", "&:hover .cell-tools, &:focus-within .cell-tools": { opacity: 1 } }}>
      <Gutter>
        <IconButton title="Zapisz i uruchom (Ctrl+Enter)" onClick={onRun} isDisabled={isRunning}>
          <MdPlayArrow />
        </IconButton>
      </Gutter>
      <CellBox>
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            flexWrap: "wrap",
            padding: "0.5rem 0.75rem 0.5rem 1rem",
            borderBottom: `1px solid ${theme.colors.border}`,
          }}
        >
          <input
            value={cell.title}
            onChange={(e) => onChange({ ...cell, title: e.target.value })}
            placeholder="Bez tytułu"
            css={{
              flex: "1 1 200px",
              minWidth: 0,
              border: "none",
              background: "transparent",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#1d1c1a",
              ":focus": { outline: "none" },
            }}
          />
          <div
            className="cell-tools"
            css={{ display: "flex", alignItems: "center", gap: "1.25rem", opacity: 0.4, transition: "opacity 120ms" }}
          >
            <VisibleToggle value={cell.visible} onChange={(visible) => onChange({ ...cell, visible })} />
            <WidthPicker value={cell.width} onChange={(width) => onChange({ ...cell, width })} />
            <div css={{ display: "flex", alignItems: "center", gap: "0.125rem" }}>
              <IconButton title="Przesuń wyżej" onClick={() => onMove(-1)} isDisabled={isFirst}>
                <MdArrowUpward />
              </IconButton>
              <IconButton title="Przesuń niżej" onClick={() => onMove(1)} isDisabled={isLast}>
                <MdArrowDownward />
              </IconButton>
              <IconButton title="Usuń blok" onClick={onRemove} color={theme.colors.danger}>
                <MdDeleteOutline />
              </IconButton>
            </div>
          </div>
        </div>
        <CodeEditor code={cell.code} onChange={(code) => onChange({ ...cell, code })} onRun={onRun} />
        {result && hasOutput(result) && (
          <div
            css={{
              padding: "0.75rem 1rem",
              borderTop: `1px solid ${theme.colors.border}`,
              opacity: isStale || isRunning ? 0.5 : 1,
              transition: "opacity 120ms",
            }}
          >
            <CellResult result={result} />
          </div>
        )}
      </CellBox>
    </div>
  );
};

const Gutter: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div css={{ width: "32px", flexShrink: 0, paddingTop: "0.25rem" }}>{children}</div>
);

const InsertLine: React.FC<{ onInsert: () => void }> = ({ onInsert }) => {
  const theme = useTheme();

  return (
    <div
      css={{
        display: "flex",
        justifyContent: "center",
        margin: "-0.5rem 0 -0.5rem 40px",
        opacity: 0,
        transition: "opacity 120ms",
        ":hover, :focus-within": { opacity: 1 },
      }}
    >
      <button
        type="button"
        onClick={onInsert}
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          padding: "0.125rem 0.625rem",
          border: `1px solid ${theme.colors.border}`,
          borderRadius: "999px",
          background: "white",
          color: theme.colors.secondaryText,
          fontSize: "0.75rem",
          cursor: "pointer",
          "&:hover": { color: theme.colors.primary, borderColor: theme.colors.primary },
        }}
      >
        <MdAdd /> Blok
      </button>
    </div>
  );
};
